import { translateValidationErrors } from '@/components/error-page/translateErrors'
import { AppErrors } from '@/components/page-specific/catering/common/types/AppErrors'
import type { MenuVendorLocationWithDeliveryRegionsFragment } from '@/generated/graphql'
import {
  runAllValidators,
  validationErrorToUserFriendlyMessage,
} from '@caterdesk/utils--gm-validation'

import * as dateFns from 'date-fns'
import type { TFunction } from 'i18next'
import _ from 'lodash'

import { UserBasketSettings } from '../../common/domain/user-basket-settings'
import { InRangeLocations } from '../../supplier-menu/hooks/useAddressInRangeCheck'

type ValidateProps = {
  inRangeVendorLocations: InRangeLocations
  userState: UserBasketSettings
  basketIsFeedrAdminApproved?: boolean
  useDeliveryTimes?: boolean
  deliveryTimes?: { start: string; end: string } | undefined
  t?: TFunction
}

export const getCutOffLocations = (locations: InRangeLocations, isCutoff5pm: boolean) =>
  locations.map((location) => {
    if (!isCutoff5pm) return location
    const locationData = { ...location }
    const locationDetails = { ...locationData.details }
    let closedDates = locationData.closingDates?.closedDates || []
    try {
      const now = new Date()
      const currentHour = now.getHours()
      let minHours = 24
      let newClosedDate = now
      closedDates = [...closedDates, dateFns.format(newClosedDate, 'yyyy-MM-dd')]
      if (currentHour >= 17) {
        newClosedDate = dateFns.add(newClosedDate, { days: 1 })
        closedDates = [...closedDates, dateFns.format(newClosedDate, 'yyyy-MM-dd')]
      }
      const dayOfWeek = dateFns.getDay(now)
      if ([5].includes(dayOfWeek)) {
        minHours -= 24
      }
      if ([6, 0].includes(dayOfWeek) && currentHour < 17) {
        newClosedDate = dateFns.add(newClosedDate, { days: 1 })
        closedDates = [...closedDates, dateFns.format(newClosedDate, 'yyyy-MM-dd')]
      }
      if ([5, 6].includes(dayOfWeek)) {
        newClosedDate = dateFns.add(newClosedDate, { days: 1 })
        closedDates = [...closedDates, dateFns.format(newClosedDate, 'yyyy-MM-dd')]
        if (dayOfWeek === 5) {
          newClosedDate = dateFns.add(newClosedDate, { days: 1 })
          closedDates = [...closedDates, dateFns.format(newClosedDate, 'yyyy-MM-dd')]
        }
      }
      minHours -= currentHour
      locationDetails.gmMinNoticePeriod = minHours
    } catch (e) {
      console.error('Error setting cutoff locations', e)
    }
    return {
      ...locationData,
      closingDates: {
        ...locationData.closingDates,
        closedDates,
      },
      details: locationDetails,
    }
  })

export const validateAll = ({
  inRangeVendorLocations,
  userState,
  basketIsFeedrAdminApproved,
  useDeliveryTimes = false,
  deliveryTimes,
  t,
}: ValidateProps): {
  newErrors: AppErrors
  chosenVendorLocations: InRangeLocations
} => {
  const inRangeLocations = _.cloneDeep(inRangeVendorLocations)

  const responses = runAllValidators({
    userState,
    inRangeLocations,
    basketIsFeedrAdminApproved,
    useDeliveryTimes,
    deliveryTimeRange: deliveryTimes,
  })

  const errors = _.mapValues(responses, (response) => {
    if (response?.result.type !== 'error') return null
    const translatedError = t ? translateValidationErrors(t, response.result.payload.code) : null
    return translatedError || validationErrorToUserFriendlyMessage(response.result.payload)
  })

  const chosenVendorLocations = responses.time?.newLocations || responses.address.newLocations

  return {
    newErrors: _.omitBy(errors, _.isNil),
    chosenVendorLocations,
  }
}
